import { supabase } from '../../../services/supabase/client'
import type { ReportType } from '../config/reportType'
import { customNodeTag } from '../../jobs/utils/nodeTag'

/**
 * Pre-generation completeness validation (FR-020).
 *
 * A report may only be generated when every inspection node carries the data
 * the PDF template presents as mandatory, and the arborist profile that signs
 * the report exists. Each issue carries a deep link so the user can jump
 * straight to the screen where it can be fixed.
 */

export type ValidationIssueType =
  | 'no-nodes'
  | 'node-missing-fields'
  | 'node-missing-photos'
  | 'profile-missing'
  | 'profile-missing-signature'

export interface ValidationIssue {
  type: ValidationIssueType
  message: string
  /** In-app route that opens the screen where the issue can be fixed */
  link: string | null
  nodeId?: string
  nodeNumber?: number
}

export interface ReportValidationResult {
  valid: boolean
  issues: ValidationIssue[]
  nodeCount: number
}

/** Node fields the PDF presents as mandatory, with user-facing labels */
const REQUIRED_NODE_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'common_name', label: 'species (common name)' },
  { key: 'dbh_cm', label: 'DBH' },
  { key: 'height_m', label: 'height' },
  { key: 'condition_rating', label: 'condition rating' },
  { key: 'risk_rating', label: 'risk rating' },
  { key: 'recommended_action', label: 'recommended action' },
]

function isMissing(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
}

function nodeLabel(nodeNumber: number, nodeTag: string | null): string {
  return nodeTag ? `Tree ${nodeNumber} (${nodeTag})` : `Tree ${nodeNumber}`
}

/**
 * Variant rules (008 R7): a full report needs at least one node, every
 * mandatory node field and a photo per node; a findings report needs none of
 * those (nodes are optional, the schedule is auto-generated) and photos only
 * when the tree-photo toggle is on. Both always need the signing profile.
 */
export async function validateReportReadiness(
  jobId: string,
  variant: ReportType = 'full_report',
  toggles: Record<string, boolean | undefined> = {}
): Promise<ReportValidationResult> {
  const issues: ValidationIssue[] = []
  const isFindings = variant === 'findings_report'
  const requireNodes = !isFindings
  const requireFields = !isFindings
  const requirePhotos = isFindings ? toggles.include_tree_photos === true : true

  // ---------------------------------------------------------------------
  // Nodes with their required fields
  // ---------------------------------------------------------------------
  const { data: nodes, error: nodesError } = await supabase
    .from('inspection_nodes')
    .select(
      'id, node_number, node_tag, common_name, dbh_cm, height_m, condition_rating, risk_rating, recommended_action'
    )
    .eq('job_id', jobId)
    .order('node_number', { ascending: true })

  if (nodesError) {
    throw new Error(`Failed to load trees for validation: ${nodesError.message}`)
  }

  if (requireNodes && (!nodes || nodes.length === 0)) {
    issues.push({
      type: 'no-nodes',
      message: 'This job has no trees — capture at least one before generating.',
      link: `/jobs/${jobId}`,
    })
    // Still check the profile below so the checklist is complete in one pass
  }

  // ---------------------------------------------------------------------
  // Photo coverage — one query for the whole job, grouped client-side
  // ---------------------------------------------------------------------
  const nodeIds = (nodes ?? []).map((n) => n.id)
  const photoCounts = new Map<string, number>()
  if (nodeIds.length > 0 && requirePhotos) {
    const { data: photos, error: photosError } = await supabase
      .from('photos')
      .select('node_id')
      .in('node_id', nodeIds)

    if (photosError) {
      throw new Error(`Failed to load photos for validation: ${photosError.message}`)
    }
    for (const photo of photos ?? []) {
      if (photo.node_id) {
        photoCounts.set(photo.node_id, (photoCounts.get(photo.node_id) ?? 0) + 1)
      }
    }
  }

  for (const node of nodes ?? []) {
    const record = node as Record<string, unknown>
    const missing = REQUIRED_NODE_FIELDS.filter((f) => isMissing(record[f.key])).map((f) => f.label)
    const label = nodeLabel(node.node_number, customNodeTag(node))
    const link = `/jobs/${jobId}/nodes/${node.id}`

    if (requireFields && missing.length > 0) {
      issues.push({
        type: 'node-missing-fields',
        message: `${label} is missing: ${missing.join(', ')}`,
        link,
        nodeId: node.id,
        nodeNumber: node.node_number,
      })
    }
    if (requirePhotos && (photoCounts.get(node.id) ?? 0) === 0) {
      issues.push({
        type: 'node-missing-photos',
        message: `${label} has no photos`,
        link,
        nodeId: node.id,
        nodeNumber: node.node_number,
      })
    }
  }

  // ---------------------------------------------------------------------
  // Assessor profile + signature. Whoever generates the report signs it with
  // their own profile (assemblePdfData does the same), so a teammate
  // generating on another arborist's job is checked against their own —
  // the job arborist's row is not readable to them (own-row RLS).
  // ---------------------------------------------------------------------
  const {
    data: { session },
  } = await supabase.auth.getSession()
  let assessorId = session?.user.id ?? null
  if (!assessorId) {
    const { data: job, error: jobError } = await supabase
      .from('jobs')
      .select('arborist_id')
      .eq('id', jobId)
      .single()
    if (jobError || !job) {
      throw new Error(`Failed to load job for validation: ${jobError?.message ?? 'not found'}`)
    }
    assessorId = job.arborist_id
  }

  let profile: { id: string; signature_image_path: string | null } | null = null
  if (assessorId) {
    const { data, error: profileError } = await supabase
      .from('arborist_profiles')
      .select('id, signature_image_path')
      .eq('id', assessorId)
      .maybeSingle()

    if (profileError) {
      throw new Error(`Failed to load arborist profile for validation: ${profileError.message}`)
    }
    profile = data
  }

  if (!profile) {
    issues.push({
      type: 'profile-missing',
      message:
        'You have no arborist profile — reports must carry your company details and qualifications.',
      link: '/profile',
    })
  } else if (isMissing(profile.signature_image_path)) {
    issues.push({
      type: 'profile-missing-signature',
      message: 'Your arborist profile has no signature image — reports must be signed.',
      link: '/profile',
    })
  }

  return { valid: issues.length === 0, issues, nodeCount: nodes?.length ?? 0 }
}
